import { useMemo } from 'react'
import { useApolloClient, useQuery, useMutation } from '@apollo/client'
import { GET_PROJECTS, GET_PROJECT } from 'graphql/queries/project/projectQueries'
import { notification } from 'antd'
import { UPGRADE_TO_PREMIUM_PROJECT_MEMBERSHIP } from 'graphql/queries/userEntities/projectMembershipQueries'
import store from 'store'
import { handleMutationException } from 'utils/error'
import { extractProject } from 'utils/extraction'

import { getUserId } from './user'
import { DEFAULT_FETCH_POLICY } from './ApiContext'
import { useTranslate } from './utils'

// Returns projects for logged user
export const useProjects = () => {
  const userId = getUserId()
  const client = useApolloClient()

  // 1) Needs to fetch data (await token if not in local storage)
  const { loading, data, error } = useQuery(GET_PROJECTS, {
    fetchPolicy: DEFAULT_FETCH_POLICY,
    variables: { userId },
    skip: !userId,
  })

  const { projects, projectIndex } = data || {}
  // If index is out of bounds (e.g. deletion, or cached)
  const projectId = data && projects && projects.length > projectIndex && projects[projectIndex].id

  // 2) Fetch details for selected project
  const projectResult = useQuery(GET_PROJECT, {
    fetchPolicy: DEFAULT_FETCH_POLICY,
    variables: { projectId },
    skip: !projectId,
  })

  const { project } = useMemo(
    () => (!projectResult || !projectResult.data ? {} : extractProject(projectResult.data)),
    [projectResult],
  )

  if (!projects) return { projects: null, loading: true, error: null }
  if (!projectResult.data) return { projects, loading: true, error: null }

  // [Project-Change]: Search project (light) on project-switch until full project is loaded
  if (projectId !== projectResult.data.project.id) {
    return findLocalProject(client, projectId, projects)
  }

  return { projects, project, loading, error }
}

// Used to find a project locally e.g. on project change,
// because useQuery might have a small delay until it updates (readQuery is synchronous)
const findLocalProject = (client, projectId, projects) => {
  try {
    // 1.) If already existend, retrieve locally (loading indicates that it is still refreshed from the server in the background)
    const { project } = extractProject(
      client.readQuery({ query: GET_PROJECT, variables: { projectId } }),
    )
    return { projects, project, loading: true, error: null }
  } catch (error) {
    // 2.) Not loaded (throws error), await from server, use "light" project from list (without captables)
    return {
      projects,
      project: projects.find((p) => p.id === projectId),
      loading: true,
      error: null,
    }
  }
}

export const useSetProject = () => {
  const userId = getUserId()
  const client = useApolloClient()

  return (projectId) => {
    // set apollo cache
    const { projects } = client.readQuery({ query: GET_PROJECTS, variables: { userId } })
    let index = projects ? projects.findIndex((p) => p.id === projectId) : 0
    if (index === -1) index = 0
    store.set('projectIndex', index)
    client.writeData({ data: { projectIndex: index } }) // projectIndexVar(index) TODO: ON_APOLLO_UPDATE (writeData deprecated)
    return Promise.resolve()
  }
}

export const useUpgradePremiumMembership = () => {
  const [upgradeToPremiumMembership, { loading }] = useMutation(
    UPGRADE_TO_PREMIUM_PROJECT_MEMBERSHIP,
  )

  const translate = useTranslate()

  return [
    (projectId) => {
      return upgradeToPremiumMembership({
        variables: { projectId },
        refetchQueries: [
          {
            query: GET_PROJECT,
            variables: { projectId },
            fetchPolicy: 'network-only',
          },
        ],
        awaitRefetchQueries: true,
      })
        .then(() => {
          notification.open({
            type: 'success',
            message: translate('project.premiumAccessGranted'),
            description: translate('project.premiumAccess.contingentUsed'),
          })
        })
        .catch((e) => {
          handleMutationException(e)
        })
    },
    { loading },
  ]
}
